import type { MouseEvent, ReactNode } from "react";
import {
  Flex,
  IconButton,
  ValueParser,
  Image,
  Text,
  Placeholder,
  Stack,
  Reveal,
  Spacing,
  EnergyLabel,
  type EnergyLabelProps,
  Icon,
  getPluralOrSingularTranslation,
} from "@huub-web/ui";
import { rem } from "@huub-web/utils";

import {
  getDisplayStreet,
  getWalterImageUrl,
  type WalterLivingInformation,
} from "../../../services";
import { energyLabels, type EnergyLabelValue } from "../../../types";

import {
  houseContainerStyle,
  imageContainerStyle,
} from "../SearchPropertySelect.css";

export type SelectedPropertyProps = {
  selected?: WalterLivingInformation;
  disabled: boolean;
  onDelete?: (e: MouseEvent<HTMLButtonElement>) => void;
  loading?: boolean;
  trailing?: ReactNode;
};

export const SelectedProperty = ({
  selected,
  disabled,
  onDelete: handleDelete,
  loading,
  trailing,
}: SelectedPropertyProps) => (
  <>
    <Stack>
      <Reveal>
        {loading && (
          <Flex gap="s">
            <Placeholder height={rem(70)} width={rem(80)} />
            <Flex direction="column" gap="xs">
              <Placeholder height={rem(22)} width={rem(80)} />
              <Placeholder height={rem(16)} width={rem(100)} />
              <Placeholder height={rem(16)} width={rem(120)} />
            </Flex>
          </Flex>
        )}
      </Reveal>
      <Reveal>
        {!loading && selected && (
          <Flex
            align="flex-start"
            justify="center"
            direction="column"
            style={{ padding: "6px" }}
          >
            <Spacing height="xs" />
            <Flex gap="s" className={houseContainerStyle}>
              {selected.listing?.photo_urls?.[0] && (
                <Image
                  src={getWalterImageUrl({
                    url: selected.listing.photo_urls[0],
                  })}
                  className={imageContainerStyle}
                />
              )}
              <Flex direction="column" gap="xs" grow>
                <Flex direction="column">
                  <Flex align="center" gap="xs" justify="space-between">
                    <Text variant="bodyL" bold ellipsis>
                      {getDisplayStreet(selected.address)}
                    </Text>
                    {handleDelete && (
                      <IconButton
                        icon="delete"
                        size="xs"
                        rounded
                        border="default"
                        onClick={handleDelete}
                        disabled={disabled}
                        variant="opaque"
                        type="button"
                        style={{ marginBottom: "-12px" }}
                      />
                    )}
                  </Flex>
                  <Text variant="bodyM" color="currentColor50">
                    {selected.address.city}
                  </Text>
                </Flex>

                {!(
                  selected.listing?.list_price === 0 ||
                  !selected.listing?.list_price
                ) && (
                  <Flex gap="xs" align="center" wrap>
                    <Text variant="bodyL">
                      <ValueParser
                        value={{
                          type: "money",
                          value: selected.listing?.list_price,
                        }}
                      />
                    </Text>
                  </Flex>
                )}

                {selected.listing?.is_listed === false && (
                  <Text variant="bodyS" color="currentColor50">
                    De woning lijkt (nog) niet in de verkoop te zijn.{" "}
                    {selected.listing?.is_listed === false &&
                      !!selected.listing?.list_price &&
                      selected.listing?.list_price > 0 && (
                        <>
                          Laatst bekende marktwaarde is{" "}
                          <ValueParser
                            value={{
                              type: "money",
                              value: selected.listing?.list_price ?? 0,
                            }}
                          />
                          .{" "}
                        </>
                      )}
                    {!!selected.property?.energy_label &&
                      energyLabels.includes(
                        selected.property?.energy_label as EnergyLabelValue,
                      ) && (
                        <>
                          Laatst bekende energielabel is{" "}
                          {selected.property?.energy_label}.{" "}
                        </>
                      )}
                  </Text>
                )}

                {selected.listing?.is_listed === true && (
                  <Flex gap="xs" align="center">
                    {selected.property?.energy_label &&
                      !["niet verplicht", "onbekend"].includes(
                        selected.property?.energy_label.toLowerCase(),
                      ) && (
                        <EnergyLabel
                          label={
                            selected.property
                              ?.energy_label as EnergyLabelProps["label"]
                          }
                          size="s"
                        />
                      )}

                    {selected.address?.rooms && selected.address?.rooms > 0 && (
                      <Flex gap="xs" align="center">
                        <Icon icon="doors" size="s2" />
                        <Text>
                          {selected.address?.rooms}{" "}
                          {getPluralOrSingularTranslation(
                            selected.address?.rooms,
                            "slaapkamer",
                            "slaapkamers",
                          )}
                        </Text>
                      </Flex>
                    )}
                  </Flex>
                )}

                {trailing}
              </Flex>
            </Flex>
            <Spacing height="xs" />
          </Flex>
        )}
      </Reveal>
    </Stack>
  </>
);
